// Pure helpers for the plan map import sheet
// (src/components/map/PlanMapImportSheet.web.tsx): crop-box geometry under
// rotation, output sizing, and the default title/source label a moderator
// sees before they've typed anything. Kept free of canvas/DOM APIs so they
// run under plain Jest — the sheet itself owns all the canvas drawing.

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type RotationDeg = 0 | 90 | 180 | 270;

export type CropCorner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

// Smallest a crop box can shrink to while being dragged, in the rotated
// canvas's own pixel space. Purely a usability floor (a sliver crop is
// never useful) — not tied to any export size.
export const MIN_CROP_SIZE = 20;

// PDF thumbnail list is capped so a 400-page CWPP doesn't render 400
// canvases; the sheet shows a note past this point.
export const MAX_PDF_THUMBNAIL_PAGES = 40;

// Step 1 render targets: a PDF page is rasterized at roughly this long side
// (pdfjs renders vector content, so upscaling to it is free); a source image
// is capped at this long side (no upscaling — an image only ever has as much
// detail as it shipped with).
export const PDF_PAGE_RENDER_LONG_SIDE = 3000;
export const IMAGE_RENDER_LONG_SIDE_CAP = 4096;

// Final export cap and JPEG quality for the cropped, rotated result.
export const EXPORT_LONG_SIDE_CAP = 4096;
export const EXPORT_JPEG_QUALITY = 0.85;

export const TITLE_MAX_LENGTH = 80;
export const SOURCE_LABEL_MAX_LENGTH = 200;
export const NOTE_MAX_LENGTH = 200;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

// --- Rotation -----------------------------------------------------------

/** Normalizes any signed multiple of 90 to one of the four cardinal values. */
export function normalizeRotation(deg: number): RotationDeg {
  const r = ((deg % 360) + 360) % 360;
  return r as RotationDeg;
}

/** Applies a +/-90 step (Rotate left / Rotate right) to the current rotation. */
export function rotateBy(current: RotationDeg, deltaDeg: 90 | -90): RotationDeg {
  return normalizeRotation(current + deltaDeg);
}

/** The bounding size of an image after rotation — width/height swap at 90/270. */
export function rotatedSize(size: Size, rotationDeg: RotationDeg): Size {
  return rotationDeg === 90 || rotationDeg === 270
    ? { width: size.height, height: size.width }
    : { width: size.width, height: size.height };
}

// --- Crop box -------------------------------------------------------------

/** The starting crop box: the whole (rotated) canvas. */
export function defaultCropRect(bounds: Size): CropRect {
  return { x: 0, y: 0, width: bounds.width, height: bounds.height };
}

/** Keeps a crop rect inside bounds and at least MIN_CROP_SIZE on each side —
 *  used defensively (e.g. the canvas shrinking under the current rect). */
export function clampCropRect(rect: CropRect, bounds: Size, minSize = MIN_CROP_SIZE): CropRect {
  const width = clamp(rect.width, Math.min(minSize, bounds.width), bounds.width);
  const height = clamp(rect.height, Math.min(minSize, bounds.height), bounds.height);
  const x = clamp(rect.x, 0, Math.max(0, bounds.width - width));
  const y = clamp(rect.y, 0, Math.max(0, bounds.height - height));
  return { x, y, width, height };
}

/** Drags the whole crop box by (dx, dy), clamped so it never leaves bounds. */
export function moveCropRect(rect: CropRect, dx: number, dy: number, bounds: Size): CropRect {
  const x = clamp(rect.x + dx, 0, Math.max(0, bounds.width - rect.width));
  const y = clamp(rect.y + dy, 0, Math.max(0, bounds.height - rect.height));
  return { ...rect, x, y };
}

/**
 * Drags one corner handle to `point` (in the same coordinate space as
 * `bounds`), keeping the opposite corner fixed. The result always stays
 * inside bounds and at least `minSize` on each side — dragging past the
 * opposite corner (or off the canvas) stalls at that floor instead of
 * flipping the box inside out.
 */
export function resizeCropCorner(
  rect: CropRect,
  corner: CropCorner,
  point: Point,
  bounds: Size,
  minSize = MIN_CROP_SIZE
): CropRect {
  const left = rect.x;
  const top = rect.y;
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;

  const fixedX = corner === 'topLeft' || corner === 'bottomLeft' ? right : left;
  const fixedY = corner === 'topLeft' || corner === 'topRight' ? bottom : top;
  const draggingLeftEdge = corner === 'topLeft' || corner === 'bottomLeft';
  const draggingTopEdge = corner === 'topLeft' || corner === 'topRight';

  const px = clamp(point.x, 0, bounds.width);
  const py = clamp(point.y, 0, bounds.height);

  const newX = clamp(
    draggingLeftEdge ? Math.min(px, fixedX - minSize) : Math.max(px, fixedX + minSize),
    0,
    bounds.width
  );
  const newY = clamp(
    draggingTopEdge ? Math.min(py, fixedY - minSize) : Math.max(py, fixedY + minSize),
    0,
    bounds.height
  );

  const x0 = Math.min(newX, fixedX);
  const x1 = Math.max(newX, fixedX);
  const y0 = Math.min(newY, fixedY);
  const y1 = Math.max(newY, fixedY);

  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

// --- Output sizing ----------------------------------------------------------

/**
 * Scales `size` so its long side is `targetLongSide`, preserving aspect
 * ratio. Downscales whenever the long side is over the target; upscales
 * only when `allowUpscale` is set (PDF page rendering wants to hit a target
 * resolution; a source image or an export crop should never be blown up
 * past what it already has).
 */
export function scaleToLongSide(
  size: Size,
  targetLongSide: number,
  opts?: { allowUpscale?: boolean }
): Size {
  const longSide = Math.max(size.width, size.height);
  if (longSide <= 0 || targetLongSide <= 0) return { width: 0, height: 0 };
  const allowUpscale = opts?.allowUpscale ?? false;
  if (longSide <= targetLongSide && !allowUpscale) {
    return { width: size.width, height: size.height };
  }
  const scale = targetLongSide / longSide;
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  };
}

// --- PDF page list ----------------------------------------------------------

/** 1-based page numbers to render thumbnails for, capped at MAX_PDF_THUMBNAIL_PAGES. */
export function visiblePdfPageNumbers(totalPages: number, cap = MAX_PDF_THUMBNAIL_PAGES): number[] {
  const count = Math.max(0, Math.min(totalPages, cap));
  return Array.from({ length: count }, (_, i) => i + 1);
}

/** How many trailing pages the thumbnail cap hides — 0 when nothing is hidden. */
export function hiddenPdfPageCount(totalPages: number, cap = MAX_PDF_THUMBNAIL_PAGES): number {
  return Math.max(0, totalPages - cap);
}

// --- Default title / source label --------------------------------------

/** File name without its extension; a name with no extension is returned as-is. */
export function stripFileExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx > 0 ? fileName.slice(0, idx) : fileName;
}

/** Title prefilled from the file name, plus " p. N" for a chosen PDF page. */
export function defaultOverlayTitle(fileName: string, page?: number): string {
  const base = stripFileExtension(fileName).trim() || 'Untitled map';
  const withPage = page != null ? `${base} p. ${page}` : base;
  return withPage.slice(0, TITLE_MAX_LENGTH);
}

/** Source label prefilled as "<file name>, page N" (or just the file name). */
export function defaultSourceLabel(fileName: string, page?: number): string {
  const label = page != null ? `${fileName}, page ${page}` : fileName;
  return label.slice(0, SOURCE_LABEL_MAX_LENGTH);
}
